import { z } from 'zod'

export type DeliveryState = 'pending' | 'sent' | 'delivered' | 'read' | 'failed'

export const ChatRoleSchema = z.enum(['Client', 'Staff', 'Guard']).catch('Staff')

export const ChatMessageKindSchema = z.enum(['User', 'System']).catch('User')

export const ChatSystemEventSchema = z.object({
  event: z.string().catch(''),
  title: z.string().nullable().optional(),
  targetUserId: z.number().nullable().optional(),
  targetName: z.string().nullable().optional(),
})

export const ChatAttachmentSchema = z.object({
  fileId: z.string(),
  name: z.string().catch(''),
  contentType: z.string().catch(''),
  size: z.number().catch(0),
  width: z.number().nullable().catch(null),
  height: z.number().nullable().catch(null),
})

export const ChatMessageContentSchema = z.object({
  body: z.string().catch(''),
  attachments: z.array(ChatAttachmentSchema).catch([]),
  mentions: z.array(z.number()).catch([]),
  replyToMessageId: z.number().nullable().catch(null),
  system: ChatSystemEventSchema.nullable().optional().catch(null),
})

export const ChatMessageSchema = z.object({
  id: z.number(),
  conversationId: z.number(),
  senderUserId: z.number(),
  senderRole: ChatRoleSchema,
  senderName: z.string().catch(''),
  clientRef: z.string().catch(''),
  sentAt: z.string(),
  isDeleted: z.boolean().catch(false),
  content: ChatMessageContentSchema.nullable().catch(null),
  kind: ChatMessageKindSchema,
})

export const ChatConversationSchema = z.object({
  id: z.number(),
  type: z.string().catch('ClientOps'),
  callsign: z.string().catch('OP'),
  title: z.string().catch(''),
  clientId: z.number().nullable().catch(null),
  lastMessageId: z.number().nullable().catch(null),
  lastMessageSenderUserId: z.number().nullable().catch(null),
  lastMessageAt: z.string().nullable().catch(null),
  lastMessagePreview: z.string().nullable().catch(null),
  lastSenderName: z.string().nullable().catch(null),
  hasUnread: z.boolean().catch(false),
  muted: z.boolean().catch(false),
  canPost: z.boolean().catch(true),
  myLastReadMessageId: z.number().nullable().catch(null),
  myLastDeliveredMessageId: z.number().nullable().catch(null),
  lastMessageDelivered: z.boolean().catch(false),
  lastMessageRead: z.boolean().catch(false),
  photoUrl: z.string().nullable().optional().catch(null),
  photoFileId: z.string().nullable().optional().catch(null),
  memberPreviews: z
    .array(
      z.object({
        userId: z.number().catch(0),
        name: z.string().catch(''),
        photoUrl: z.string().nullable().optional().catch(null),
        photoFileId: z.string().nullable().optional().catch(null),
      }),
    )
    .nullable()
    .optional()
    .catch(null),
})

export const DirectoryEntrySchema = z.object({
  userId: z.number(),
  name: z.string().catch(''),
  employeeType: z.string().catch(''),
  photoUrl: z.string().nullable().optional().catch(null),
  photoFileId: z.string().nullable().optional().catch(null),
})

export const ChatMemberSchema = z.object({
  userId: z.number().nullable().catch(null),
  name: z.string().catch(''),
  role: ChatRoleSchema,
  lastDeliveredMessageId: z.number().nullable().catch(null),
  lastReadMessageId: z.number().nullable().catch(null),
  photoUrl: z.string().nullable().optional().catch(null),
  photoFileId: z.string().nullable().optional().catch(null),
})

export const ChatConversationDetailSchema = z.object({
  summary: ChatConversationSchema,
  clientUserCount: z.number().catch(0),
  staffCount: z.number().catch(0),
  members: z.array(ChatMemberSchema).catch([]),
  canManage: z.boolean().catch(false),
})

export const ChatUploadSchema = z.object({
  attachmentId: z.number(),
  fileId: z.string(),
  name: z.string().catch(''),
  contentType: z.string().catch(''),
  size: z.number().catch(0),
  width: z.number().nullable().catch(null),
  height: z.number().nullable().catch(null),
})

export const ChatDownloadUrlsSchema = z.object({
  urls: z.record(z.string(), z.string()).catch({}),
})

export const BroadcastSchema = z.object({
  id: z.number(),
  audienceType: z.string().catch('Client'),
  audienceId: z.number().nullable().catch(null),
  audienceName: z.string().catch(''),
  body: z.string().catch(''),
  sentBy: z.number().catch(0),
  sentByName: z.string().catch(''),
  sentOn: z.string(),
  recipientCount: z.number().catch(0),
  readCount: z.number().catch(0),
})

export const BroadcastShiftGuardSchema = z.object({
  userId: z.number(),
  name: z.string().catch(''),
  onDuty: z.boolean().catch(false),
})

export const BroadcastShiftSchema = z.object({
  shiftId: z.number(),
  title: z.string().catch(''),
  siteName: z.string().catch(''),
  startTime: z.string(),
  endTime: z.string(),
  overtime: z.boolean().catch(false),
  guards: z.array(BroadcastShiftGuardSchema).catch([]),
})

export const BroadcastRecipientSchema = z.object({
  userId: z.number(),
  name: z.string().catch(''),
  readOn: z.string().nullable().catch(null),
  acknowledgedOn: z.string().nullable().catch(null),
})

export const BroadcastDetailSchema = z.object({
  broadcast: BroadcastSchema,
  recipients: z.array(BroadcastRecipientSchema).catch([]),
})

export const BroadcastReachSchema = z.object({
  recipientCount: z.number().catch(0),
  description: z.string().catch(''),
})

export type ChatRole = z.infer<typeof ChatRoleSchema>
export type ChatMessageKind = z.infer<typeof ChatMessageKindSchema>
export type ChatSystemEvent = z.infer<typeof ChatSystemEventSchema>
export type ChatAttachment = z.infer<typeof ChatAttachmentSchema>
export type ChatMessageContent = z.infer<typeof ChatMessageContentSchema>
export type ChatMessage = z.infer<typeof ChatMessageSchema>
export type ChatConversation = z.infer<typeof ChatConversationSchema>
export type DirectoryEntry = z.infer<typeof DirectoryEntrySchema>
export type ChatMember = z.infer<typeof ChatMemberSchema>
export type ChatConversationDetail = z.infer<typeof ChatConversationDetailSchema>
export type ChatUpload = z.infer<typeof ChatUploadSchema>
export type Broadcast = z.infer<typeof BroadcastSchema>
export type BroadcastRecipient = z.infer<typeof BroadcastRecipientSchema>
export type BroadcastShift = z.infer<typeof BroadcastShiftSchema>
export type BroadcastShiftGuard = z.infer<typeof BroadcastShiftGuardSchema>
export type BroadcastDetail = z.infer<typeof BroadcastDetailSchema>
export type BroadcastReach = z.infer<typeof BroadcastReachSchema>

export type SendMessageRequest = {
  clientRef: string
  body: string
  attachmentIds: number[]
  mentions: number[]
  replyToMessageId: number | null
}

export type DeliveredItem = { conversationId: number; lastMessageId: number }

export type CreateGroupRequest = { title: string; userIds: number[] }
export type AddGroupMembersRequest = { userIds: number[] }
